// @flow
import {Map} from 'immutable';
import moment from 'moment';
import {setUpStore, Store} from 'client/framework';
import appStore from 'client/AppStore';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';
import assignmentsListActions from './AssignmentsList.actions';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import type {List} from 'immutable';

class AssignmentsListStore extends Store {
  selectableDateRangeStore: SelectableDateRangeStore;

  constructor(name: string) {
    super(name);
    this.selectableDateRangeStore = setUpStore(
      SelectableDateRangeStore,
      'AssignmentsListSelectableDateRangeStore'
    );
    const initialValidDates = Map({
      startDate: null,
      endDate: null
    });
    this.setInitialData(
      Map({
        classroomId: null,
        teacherId: null,
        lastValidDates: initialValidDates,
        assignmentToDelete: null,
        deleteAssignmentPromise: null
      })
    );
    this.handle(
      assignmentsListActions.SET_ASSIGNMENT_TO_DELETE,
      this.setProperty('assignmentToDelete')
    );
    this.handle(assignmentsListActions.SET_LAST_VALID_DATES, this._setLastValidDates);
    this.handle(assignmentsListActions.DELETE_ASSIGNMENT, this._deleteAssignment);
    this.handle(assignmentsListActions.INITIALIZE_TEACHER_AND_CLASSROOM_IDS, this._initializeIds);
  }

  _initializeIds(initialData: Map<string, *>) {
    this.writeData((state) => {
      return state
        .set('classroomId', initialData.get('classroomId'))
        .set('teacherId', initialData.get('teacherId'));
    });
  }

  _setLastValidDates({startDate, endDate}: {startDate: ?string, endDate: ?string}) {
    this.writeData(
      'lastValidDates',
      Map({
        startDate: startDate
          ? moment(startDate).startOf('day').subtract(1, 'second').valueOf()
          : null,
        endDate: endDate ? moment(endDate).endOf('day').valueOf() : null // Includes midnight
      })
    );
  }

  _deleteAssignment() {
    const deleteAssignmentPromise = this.getAssignmentToDelete().delete();
    this.writeData('deleteAssignmentPromise', deleteAssignmentPromise);
    deleteAssignmentPromise.then(() => {
      invalidatePartialInterest({resourcePath: ['assignments_v3']});
      this.writeData('deleteAssignmentPromise', null);
    });
  }

  getClassroomId(): ?string {
    return this.readData('classroomId');
  }

  filterDatesAreSet(): boolean {
    return this.getLastValidDates().every((date) => Boolean(date));
  }

  getSelectableDateRangeStore = () => this.selectableDateRangeStore;

  getLastValidDates = () => this.readData('lastValidDates');

  getAssignmentToDelete = () => this.readData('assignmentToDelete');

  getDeleteAssignmentPromise = () => this.readData('deleteAssignmentPromise');
}

export default new AssignmentsListStore('AssignmentsListStore');
